import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { AxiosError, AxiosRequestConfig } from 'axios';

import Axios from '../../foundation/config/axios';

// RTK Query lets us define a custom baseQuery if we don't want to use
// fetchBaseQuery.  A baseQuery is just an async function that receives a
// request description and must return either { data } on success or
// { error } on failure.
//
// We delegate to our existing Axios instance so we keep every existing
// interceptor: CSRF token injection, session-cookie auth, and centralized
// error handling.  Axios already has baseURL set, so endpoints only need
// the relative path (e.g. `/variantdata/variant/…`).

export interface AxiosBaseQueryArgs {
  url: string;
  method?: AxiosRequestConfig['method'];
  data?: AxiosRequestConfig['data'];
  params?: AxiosRequestConfig['params'];
  headers?: AxiosRequestConfig['headers'];
  responseType?: AxiosRequestConfig['responseType'];
  signal?: AbortSignal;
}

export interface AxiosBaseQueryError {
  status?: number;
  data: unknown;
}

export const axiosBaseQuery: BaseQueryFn<
  AxiosBaseQueryArgs,
  unknown,
  AxiosBaseQueryError
> = async ({
  url,
  method = 'GET',
  data,
  params,
  headers,
  responseType,
  signal,
}) => {
  try {
    // Forward the request to our pre-configured Axios instance
    // BIOFRO-80 ABS review: pass the RTK Query AbortSignal through to Axios
    // so that endpoint.skipToken / RTK Query cancellation actually aborts
    // the in-flight HTTP request (Redux Toolkit best practice).
    const result = await Axios({
      url,
      method,
      data,
      params,
      headers,
      responseType,
      signal,
      // Django Ninja expects array params as repeated keys: ?key=a&key=b
      // Axios defaults to comma-separated: ?key=a,b — which Django reads as one string "a,b".
      // use axios built-in paramsSerializer function to handle this correctly
      paramsSerializer: { indexes: null },
    });
    // RTK Query expects { data } on success
    return { data: result.data };
  } catch (axiosError) {
    const err = axiosError as AxiosError;
    // RTK Query expects { error } on failure
    return {
      error: {
        status: err.response?.status,
        data: err.response?.data || err.message,
      },
    };
  }
};
